import React, { useEffect, useState } from 'react';
import { FiHome, FiLayers, FiBook, FiFileText, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { publisherSubjectAPI } from '../../api/publisherEndpoints';
import {
  DashboardContainer,
  DashboardHeader,
  SectionTitle,
  GradeGrid,
  GradeCard,
  GradeCardHeader,
  GradeCardBody,
  GradeCardFooter,
  GradeActions,
  ActionButton,
  EmptyState,
  FilterBar,
  SearchInput,
  FilterSelect,
} from '../dashboards/Dashboard.styles';

const PublisherSubjects = () => {
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true,
  });

  const [filters, setFilters] = useState({
    search: '',
    code: '',
    is_active: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 20,
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/publisher' },
    { icon: <FiFileText />, label: 'Publications', path: '/publisher/publications' },
    { icon: <FiLayers />, label: 'Grades', path: '/publisher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/publisher/subjects' },
  ];

  useEffect(() => {
    fetchSubjects();
  }, [filters]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await publisherSubjectAPI.getSubjects(filters);
      setSubjects(response.data?.subjects || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      showToast('error', 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', description: '', is_active: true });
    setEditingSubject(null);
    setShowModal(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await publisherSubjectAPI.createSubject({
        name: formData.name,
        code: formData.code,
        description: formData.description,
      });
      showToast('success', 'Subject created successfully');
      resetForm();
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create subject');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingSubject) return;
    setSubmitting(true);

    try {
      await publisherSubjectAPI.updateSubject(editingSubject.id, {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        is_active: formData.is_active,
      });
      showToast('success', 'Subject updated successfully');
      resetForm();
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to update subject');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name || '',
      code: subject.code || '',
      description: subject.description || '',
      is_active: subject.is_active !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (subjectId, name) => {
    if (!window.confirm(`Delete "${name}"? This will deactivate the subject.`)) return;

    try {
      await publisherSubjectAPI.deleteSubject(subjectId);
      showToast('success', 'Subject deleted successfully');
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to delete subject');
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading subjects..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Subjects</h1>
            <p>Maintain subject codes, names, and descriptions</p>
          </div>
          <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
            New Subject
          </Button>
        </DashboardHeader>

        <FilterBar>
          <SearchInput
            placeholder="Search subjects..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
          <SearchInput
            placeholder="Filter by code..."
            value={filters.code}
            onChange={(e) => setFilters({ ...filters, code: e.target.value, page: 1 })}
          />
          <FilterSelect
            value={filters.is_active}
            onChange={(e) => setFilters({ ...filters, is_active: e.target.value, page: 1 })}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value, page: 1 })}
          >
            <option value="name">Sort by Name</option>
            <option value="created_at">Sort by Created</option>
            <option value="updated_at">Sort by Updated</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_order}
            onChange={(e) => setFilters({ ...filters, sort_order: e.target.value, page: 1 })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </FilterSelect>
        </FilterBar>

        <SectionTitle>Subject Catalog</SectionTitle>

        {subjects.length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No subjects found</h3>
            <p>Create your first subject to organize publications</p>
            <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
              Create Subject
            </Button>
          </EmptyState>
        ) : (
          <GradeGrid>
            {subjects.map((subject) => (
              <GradeCard key={subject.id}>
                <GradeCardHeader>
                  <div>
                    <h3>{subject.name}</h3>
                    <span className="badge">{subject.code || 'CODE'}</span>
                  </div>
                </GradeCardHeader>
                <GradeCardBody>
                  <p>{subject.description || 'No description provided.'}</p>
                  <div className="meta">
                    <span>{subject.is_active ? 'Active' : 'Inactive'}</span>
                    <span>Updated {new Date(subject.updated_at || subject.created_at).toLocaleDateString()}</span>
                  </div>
                </GradeCardBody>
                <GradeCardFooter>
                  <GradeActions>
                    <ActionButton title="Edit" onClick={() => handleEdit(subject)}>
                      <FiEdit />
                    </ActionButton>
                    <ActionButton title="Delete" onClick={() => handleDelete(subject.id, subject.name)}>
                      <FiTrash2 />
                    </ActionButton>
                  </GradeActions>
                </GradeCardFooter>
              </GradeCard>
            ))}
          </GradeGrid>
        )}

        {pagination?.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <Button
              variant="ghost"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="ghost"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={resetForm}
          title={editingSubject ? 'Edit Subject' : 'Create Subject'}
          size="medium"
        >
          <form onSubmit={editingSubject ? handleUpdate : handleCreate}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                label="Subject Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Mathematics"
                required
              />
              <Input
                label="Subject Code"
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="e.g., MATH"
                required
              />
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the subject"
                  style={{
                    width: '100%',
                    minHeight: '110px',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '14px',
                    resize: 'vertical',
                    background: '#F9FAFB',
                  }}
                />
              </div>

              {editingSubject && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Status
                  </label>
                  <select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button variant="ghost" type="button" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" loading={submitting}>
                  {editingSubject ? 'Update Subject' : 'Create Subject'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default PublisherSubjects;
